import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, Clock, ArrowRight, Loader2, ExternalLink, Globe } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { API } from "@/App";

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");
  const redirectUrl = searchParams.get("redirect_url");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else if (bookingId) {
      fetchBookingDetails();
    } else {
      setLoading(false);
      setPaymentStatus({ status: "success" });
    }
  }, [sessionId, bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBookingDetails(response.data);
      setPaymentStatus({ status: "success" });
    } catch (error) {
      console.error("Error fetching booking:", error);
      setPaymentStatus({ status: "success" }); // Still show success even if details fail
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async () => {
    if (pollCount >= 10) {
      setLoading(false);
      setPaymentStatus({ status: "timeout" });
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setPaymentStatus({ ...response.data, status: "success" });
        setLoading(false);
      } else if (response.data.status === "expired") {
        setPaymentStatus({ status: "expired" });
        setLoading(false);
      } else {
        // Continue polling
        setTimeout(() => {
          setPollCount(prev => prev + 1);
          pollPaymentStatus();
        }, 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setLoading(false);
      setPaymentStatus({ status: "error" });
    }
  };

  // Generate Google Calendar link for the customer
  const generateGoogleCalendarLink = () => {
    if (!bookingDetails) return null;
    
    // Remove trailing Z to treat as local time
    const cleanStartDateTime = bookingDetails.start_datetime?.replace(/Z$/, '');
    const cleanEndDateTime = bookingDetails.end_datetime?.replace(/Z$/, '');
    const startDate = new Date(cleanStartDateTime);
    const endDate = new Date(cleanEndDateTime);
    
    const formatDateForGoogle = (date) => {
      return date.toISOString().replace(/-|:|\.\d{3}/g, '');
    };
    
    const title = encodeURIComponent(`Appuntamento: ${bookingDetails.business_name || 'Prenotazione'}`);
    const dates = `${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}`;
    const details = encodeURIComponent(
      `Servizi: ${bookingDetails.services_details?.map(s => s.name).join(', ') || ''}\n` +
      `Prenotazione confermata`
    );
    const location = encodeURIComponent(bookingDetails.business_address || '');
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&location=${location}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Verifica Pagamento...
            </h2>
            <p className="text-slate-500">
              Stiamo verificando il tuo pagamento, attendi qualche secondo.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus?.status === "error" || paymentStatus?.status === "timeout") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Verifica in corso
            </h2>
            <p className="text-slate-500 mb-6">
              Il pagamento potrebbe essere ancora in elaborazione. Riceverai un'email di conferma a breve.
            </p>
            <Link to="/">
              <Button variant="outline">
                Torna alla Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const googleCalendarLink = generateGoogleCalendarLink();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="booking-success-page">
      <Card className="max-w-md w-full animate-fadeIn">
        <CardContent className="py-12 text-center">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Prenotazione Confermata!
          </h2>
          <p className="text-slate-500 mb-6">
            La tua prenotazione è stata completata con successo. Riceverai un'email di conferma con tutti i dettagli.
          </p>
          
          {paymentStatus?.amount && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-slate-500">Importo pagato</p>
              <p className="text-2xl font-bold text-slate-900">
                €{paymentStatus.amount.toFixed(2)}
              </p>
            </div>
          )}

          {bookingDetails && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span>
                  {format(new Date(bookingDetails.start_datetime?.replace(/Z$/, '')), "EEEE d MMMM yyyy", { locale: it })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-slate-400" />
                <span>
                  {format(new Date(bookingDetails.start_datetime?.replace(/Z$/, '')), "HH:mm", { locale: it })} - 
                  {format(new Date(bookingDetails.end_datetime?.replace(/Z$/, '')), "HH:mm", { locale: it })}
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {googleCalendarLink && (
              <a href={googleCalendarLink} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full" data-testid="add-to-calendar-btn">
                  <Calendar className="h-4 w-4 mr-2" />
                  Aggiungi a Google Calendar
                  <ExternalLink className="h-3 w-3 ml-2" />
                </Button>
              </a>
            )}
            {redirectUrl && (
              <a href={redirectUrl}>
                <Button className="w-full" data-testid="redirect-site-btn">
                  <Globe className="h-4 w-4 mr-2" />
                  Torna al sito
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </a>
            )}
            {!redirectUrl && (
              <Link to="/">
                <Button className="w-full" data-testid="back-home-btn">
                  Torna alla Home
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingSuccess;
